import find from 'lodash/find';
import compose from 'lodash/fp/compose';
import _ from 'lodash';
import TWIAJobHelper from 'ent-capability-quoteandbind-common-react/util/TWIAJobHelper';

const BUILDING_OPTION_NA = {
    key: 0,
    value: 'N/A'
};
// value: $filter('translate')('twia.quoteandbind.views.additional-info.document-upload.building.option.N/A')};

const allBuildings = locations => _.flatMap(locations, loc => loc.buildings)
const findByPublicId = publicID => obj => find(obj, o => o.publicID === publicID) || {};

const findBuilding = (publicID, obj) => find(obj, o => o.publicID === publicID) || {}

const allLocations = model => model.lobData.commercialProperty.coverables.locations;
const isNewOnCurrentTerm = o => o.isNewOnCurrentTerm_TWIA;

const isBuildersRisk = b => b.coverageForm === 'TWCR';

const findBuildingByPublicID = (publicID, locations) => compose(findByPublicId(publicID), allBuildings)(locations);
const findLocationByPublicID = (publicID, locations) => compose(findByPublicId(publicID))(locations);
const isNewBuildingOnCurrentTerm = (buildingPublicID, model) => {
    const locations = allLocations(model);
    const buildings = allBuildings(locations);
    const building = findBuilding(buildingPublicID, buildings)

    return isNewOnCurrentTerm(building);
}
const isCoverageFormBuildersRisk = (buildingPublicID, model) => compose(isBuildersRisk, findByPublicId(buildingPublicID), allBuildings, allLocations)(model);

export default class TWIACPBuildingLocationsHelper {
    /**
     * Returns the list of all buildings on the locations.
     * @param {LocationDTO[]} locations
     * @returns {BuildingDTO[]}
     */
    static allBuildings(locations) {
        return allBuildings(locations);
    }

    /**
     * find Location by publicID
     * @param {string}  publicID
     * @param {LocationDTO[]} locations
     * @returns {LocationDTO}
     */
    static findLocationByPublicID(publicID, locations) {
        return findLocationByPublicID(publicID, locations);
    }

    /**
     * find Building by publicID
     * @param {string}  publicID
     * @param {LocationDTO[]} locations
     * @returns {BuildingDTO}
     */
    static findBuildingByPublicID(publicID, locations) {
        return findBuildingByPublicID(publicID, locations);
    }

    /**
     * find location and building by publicIDs
     * @param {string} locationPublicID
     * @param {string} buildingPublicID
     * @param {LocationDTO[]} locations
     * @returns {{location: {LocationDTO}, building: {BuildingDTO}}}
     */
    static findLocationAndBuildingByPublicID(locationPublicID, buildingPublicID, locations) {
        return {
            location: findLocationByPublicID(locationPublicID, locations),
            building: findBuildingByPublicID(buildingPublicID, locations)
        };
    }

    /**
     * Returns true if the building is new on current term i.e. does not exist on prior terms
     * @param {string} buildingPublicID
     * @param {SubmissionDTO|RenewalDTO} model
     * @returns {Boolean} true if the building is new on current term i.e. does not exist on prior terms
     */
    static isNewBuildingOnCurrentTerm(buildingPublicID, model) {
        return isNewBuildingOnCurrentTerm(buildingPublicID, model);
    }

    /**
     * @param {string}buildingPublicID
     * @param {submissionDTO|RenewalDTO} model
     * @returns {boolean} Returns true is the building coverage form is builders risk.
     */
    static isCoverageFormBuildersRisk(buildingPublicID, model) {
        return isCoverageFormBuildersRisk(buildingPublicID, model);
    }

    /**
     * US18867 - Commercial Renewal - Building Sub-flow - Construction
     * US12609 - Commercial Policy Change - Construction Details
     * For Builder's Risk, existing building delete not allowed for Renewal and PolicyChange transactions.
     * @param {string} buildingPublicID
     * @param {submissionDTO|RenewalDTO} model
     * @returns {boolean}
     */
    static isBuildingDeleteAllowed(buildingPublicID, model) {
        return !(TWIAJobHelper.isEitherRenewalOrPolicyChange(model) && isCoverageFormBuildersRisk(buildingPublicID, model) );
    }

    /**
     * US18867 - Commercial Renewal - Building Sub-flow - Construction
     * US12609 - Commercial Policy Change - Construction Details
     * Many fields on the existing coverable are not editable by the agent-user during renewal or policychange transaction.
     * @param {string} buildingPublicID
     * @param {submissionDTO|RenewalDTO} model
     * @returns {boolean|*} true, if the building exists on prior term and the current transactions is a renewal.
     */
    static isBuildingExistsOnBasedOn(buildingPublicID, model) {
        return TWIAJobHelper.isEitherRenewalOrPolicyChange(model) && !isNewBuildingOnCurrentTerm(buildingPublicID, model) ;
    }

    // 2021-06-01 - Debra Frum - US18923 - Add Documents to Agent Activities
    static getBuildingsFromLocations(locations) {
        return _(locations)
            .map(loc => {
                // 2021-10-14 - Debra Frum - US20706 - Locations with No Physical Address
                //   Replace custom formatting of building and location with use of DisplayName fields of building and
                //   location in error messages
                // const address = `${loc.address.addressLine1}, ${loc.address.city}, ${loc.address.state}, ${loc.address.postalCode}`;
                const address = loc.displayName;

                return loc.buildings.map(building => ({
                    fixedID: building.fixedID,
                    publicID: building.publicID,
                    buildingID: building.buildingNumber,
                    description: building.description,
                    location: loc,
                    locationNum_TWIA: loc.locationNum_TWIA,
                    address
                }));
            })
            .flatten()
            .sortBy('buildingID') // Assumption is that public id grows
            .sortBy((location) => location.locationNum_TWIA)
            .valueOf();
    }

    // 2021-07-14 - Debra Frum - US11961 - Policy Documents - Policy Documents
    static getBuildingOptionNA() {
        return BUILDING_OPTION_NA;
    }

    // 2021-06-01 - Debra Frum - US18923 - Add Documents to Agent Activities
    /**
     * Returns a drop-down option list of buildings
     * Example of option format: "1: Building E (5700 S Mopac Expy, Austin, TX, 78749)"
     * @param buildings
     * @returns {*[]}
     */
    static getBuildingOptions(buildings) {
        const options = [];

        //  let option = $filter('translate')('twia.quoteandbind.views.additional-info.document-upload.building.option.N/A');
        options.push(BUILDING_OPTION_NA);

        // 2021-03-25 - Debra Frum -  US11735 - Commercial Quote - Additional Info - Validate Uploaded Documents
        // Building FixedID will be saved as metadata on documents
        if (!_.isNil(buildings)) {
            buildings.forEach((building) => {
                const option = `${building.buildingID}: ${_.isNil(building.description) ? '' : building.description} (${building.address})`;

                options.push({key: building.fixedID, value: option});
            });
        }

        return options;
    }

    // 2021-10-14 - Debra Frum - US20706 - Locations with No Physical Address
    /**
     * Returns a list of location address lines formatted as a Full Address which includes Lot, Block, and Section
     * on a single line
     * @param location
     * @returns {string}
     */
    static getFullAddress(location) {
        // Format Lot, Block, and Section,if defined, on a single line
        const locationAdditionalFieldsTwia = location.locationAdditionalFields_TWIA;
        let nonPhysicalLine = '';

        if (!_.isNil(locationAdditionalFieldsTwia.locationLot_TWIA)) {
            nonPhysicalLine += `Lot ${locationAdditionalFieldsTwia.locationLot_TWIA}, `;
        }

        if (!_.isNil(locationAdditionalFieldsTwia.locationBlock_TWIA)) {
            nonPhysicalLine += `Block ${locationAdditionalFieldsTwia.locationBlock_TWIA}, `;
        }

        if (!_.isNil(locationAdditionalFieldsTwia.locationSection_TWIA)) {
            nonPhysicalLine += `Section ${locationAdditionalFieldsTwia.locationSection_TWIA}, `;
        }

        // Remove the comma from the end
        nonPhysicalLine = nonPhysicalLine.slice(0, -2);

        // Create list of location address fields with the location number stripped off
        const locationAddressDisplayNameTwia = location.displayLocationAddressDTO_TWIA.displayName;
        const locationAddressFieldsTwia = locationAddressDisplayNameTwia.substring(locationAddressDisplayNameTwia.indexOf(':') + 2).split(',');

        // Concatenate Lot/Block/Section line with the list of location address fields
        const fullAddressTwia = [];

        if (!_.isNil(nonPhysicalLine)) {
            fullAddressTwia.push(...[nonPhysicalLine]);
        }

        fullAddressTwia.push(...locationAddressFieldsTwia);

        return fullAddressTwia;
    }
}
